/**
 * FERP Portal - Root Application Component
 *
 * Implements Three-Layer Interface:
 * - User View (Layer 1): "My work" - tasks, deadlines, actions
 * - Admin View (Layer 2): "Team work" - assignments, queues, metrics
 * - Architect View (Layer 3): "Primitive composition" - 25 building blocks
 *
 * FERP Principles:
 * - Data-driven: Subgraph-specific routes generated from SubgraphMetadata
 * - NO hardcoded subgraph-specific routes (dynamic generation)
 * - Core routes (primitives, dashboard) remain static
 *
 * CONSTRAINTS:
 * - NO MUI components (Mantine v8.x only)
 * - NO Redux/Zustand/MobX (Apollo Client is single source of truth)
 * - NO hardcoded values (all config from backend/metadata)
 * - NO direct useQuery<Record<string, any>>(all data access through Repository Pattern)
 * - NO client-side ID generation (backend Snowflake IDs only)
 *
 * Route Organization by 25-Primitive Lifecycle:
 * - DESIGN: /architect/workflows, contexts, constraints, dependencies, forms
 * - CONFIGURE: /admin/actors, policies, permissions, queues, schedules
 * - EXECUTE: /tasks, states, events, sagas, dagnodes
 * - MONITOR: /admin/metrics, alerts, audits, reports, notifications
 * - GOVERN: /admin/contracts, compliance, risks, evidence, versions
 */

import { useEffect, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Center, Loader, Text, Stack } from '@mantine/core';

import { AuthProvider, useAuth } from './auth/AuthContext';
import { SimulationProvider } from './auth/SimulationContext';
import { registerDefaultTabRenderers } from './components/entity-detail/registerDefaultRenderers';

// DRR-165: Register FormSchema-driven tab renderers at app init
registerDefaultTabRenderers();
import { useMyViewLayerQuery } from '@/generated/graphql';
import { ActorNameProvider } from './contexts/ActorNameContext';
import { FhirLabelsProvider } from './contexts/FhirLabelsContext';
import { ViewContextProvider } from './contexts/ViewContext';
import { MainLayout } from './layouts/MainLayout';
import { PageErrorBoundary } from './components/PageErrorBoundary';
import { LoginPage } from './pages/LoginPage';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { DashboardPage } from './pages/DashboardPage';
import { SubjectDashboardPage } from './pages/subject-dashboard';
import { WorkSurfacePageConnected } from './pages/work-surface';

// DESIGN Phase Primitives (Architect Layer)
import { WorkflowListPage, WorkflowRegistryPage, LlmWorkflowEditorPage, MasterWorkflowPage, WorkflowDetailPage, WorkflowStudioPage } from './pages/workflows';
import { LlmFormSchemaEditorPage, FormGalleryPage } from './pages/forms';

// DESIGN Phase Primitives (continued)
import { ConstraintListPage } from './pages/constraints';
import { DependencyListPage } from './pages/dependencies';
import { TemplateListPage } from './pages/templates';

// CONFIGURE Phase Primitives (Admin Layer)
import { FormSchemaAdminPage, PrimitiveConstellationPage, SourceMapReviewPage, SourceMapListPage, ProductCatalogPage, RoleAccessComparisonPage, RBACDashboardPage, WitnessQueuePage, SagaMonitoringPage, ExecutiveSafetyPage, CacheManagementPage, CLevelAuditPage, JobMonitoringPage, QueueManagerPage, SystemHealthPage, OptimizationPage, TaskAnalyticsPage, ProcessAnalyticsPage, ApprovalQueuePage, EmergencyParticipantPage, SafetyEventsPage, ValidationReviewPage, MultiLLMComparisonPage, PolicyAnalyticsPage, RegulatoryGovernancePage, ProductionMonitoringPage, WorkflowCompositionPage, ActorRuntimePage, ActorDescriptorPage, DraftManagementPage, ComprehensiveDashboardPage, SecureSystemHealthPage, TabulatorLifecyclePage, PrimitiveCompositionPage, PrimitiveRegistryPage, DomainConfigPage, ComplianceConfigPage, GovernanceDashboardPage, GovernanceContextPage, SagaMetricsPage, MonitoringDashboardPage, RoleManagementPage, IntegrationManagementPage, ResourceManagementPage, EscalationManagementPage, ScheduleManagementPage, PolicyManagementPage, ExpertRoutingPage, TaskApprovalPage, ApprovalPoliciesPage, TabulatorEntityPage, MasterWorkflowDashboardPage, TemplateWorkflowPage, ConversionProgressPage, ExtractionReviewPage, DashboardEnhancedPage, GraphQLWidgetsPage, LifecycleWidgetsPage, CompositionEnginePage, ComplexProcessPage, PerformanceOptimizationPage, ChaosTemplatePage, Sprint7DashboardPage, AmbientDashboardPage, QapiDashboardPage, ComplianceDashboardPage, ProductionSystemHealthPage, ConfigProposalQueuePage, SearchPage, TerminologyPage, AuthObservabilityPage } from './pages/admin';
import SubgraphEntityManagerPage from './pages/admin/SubgraphEntityManagerPage';
import { SpatialPinningEditor } from './pages/admin/SpatialPinningEditor';
import { PolicyListPage } from './pages/policies';
import { ActorListPage, ActorManagementPage } from './pages/actors';
import { RoleListPage } from './pages/roles';
import { ScheduleListPage } from './pages/schedules';

// EXECUTE Phase Primitives (User Layer - Tasks, States, Events, Sagas, DAGNodes)
import { TaskListPage, TaskDetailPage, TaskTemplatesPage } from './pages/tasks';
import { WorkflowStateView } from './pages/workflow-state';
import { StateListPage } from './pages/states';
import { EventListPage } from './pages/events';
import { SagaListPage } from './pages/sagas';
import { DAGNodeListPage } from './pages/dagnodes';

// EXECUTE Phase Primitives (continued)
import { InstanceListPage, InstanceDetailPage } from './pages/instances';

// DRR-241: Hiring Pipeline
import { ApplicantStatusPage } from './pages/hiring/ApplicantStatusPage';

// MONITOR Phase Primitives (Admin Layer - Metrics, Alerts, Reports)
import { MetricListPage } from './pages/metrics';
import { AlertListPage, BeaconDashboardPage } from './pages/alerts';
import { ReportListPage } from './pages/reports';
import { NotificationListPage } from './pages/notifications';
import { TransactionListPage } from './pages/transactions';

// Best Practice Intelligence (DRR-156)
import { RecommendationsPage } from './pages/recommendations';
// GOVERN Phase Primitives (Admin Layer - Compliance, Risks, Evidence)
import { ComplianceListPage, ReadinessDashboard, ComplianceEvidencePage, PracticeSurveyRunner, PhotoComplianceUpload, RuleSetViewerPage, RuleSetAdminPage, StandardsPackPage, AssessmentEvidenceDashboard, ComplianceManualPage, AuditFormsPage } from './pages/compliance';
import { RiskListPage } from './pages/risks';
import { EvidenceListPage } from './pages/evidence';
import { ContractListPage } from './pages/contracts';

// MONITOR Phase Primitives (continued)
import { TraceListPage } from './pages/traces';
import { CheckpointListPage } from './pages/checkpoints';

// OPTIMIZE Phase Primitives
import { AuditListPage } from './pages/audits';
import { AnalyticsListPage } from './pages/analytics';
import { ReconciliationDashboardPage } from './pages/reconciliation';
import { ResourceListPage } from './pages/resources';
import { IntegrationListPage } from './pages/integrations';

// CONFIGURE Phase Primitives (continued)
import { ValidationListPage } from './pages/validations';
import { QueueListPage } from './pages/queues';

// Sync Dashboard (DRR-113: Chain sync session management)
import { SyncDashboardPage } from './pages/sync';

// Config Type List Pages (DRR-125 Sprint 4: tg-, cs-, rn-)
import { TaskGroupListPage, TaskGroupDetailPage, TaskGroupProgressPage } from './pages/task-groups';
import { CascadeListPage } from './pages/cascades';
import { RegNodeListPage } from './pages/regnodes';

// Config Type List Pages (DRR-125 Part 12: st-, ft-)
import { SettingListPage } from './pages/settings';
import { FormTemplateListPage } from './pages/form-templates';

// Config Browser Pages (gap remediation: seeded configs now browseable)
import { BriefingTemplateListPage } from './pages/briefing-templates';
import { GovernanceListPage } from './pages/governance';
import { UiSchemaListPage } from './pages/ui-schemas';
import { LensCatalogPage } from './pages/lenses';
import { TenantBrandingAdminPage } from './pages/branding';
import { DashboardSchemaListPage } from './pages/dashboard-schemas';
import { FormSchemaOverrideListPage } from './pages/form-schema-overrides';
import { EdiTemplateListPage } from './pages/edi-templates';

// Backend feature pages (gap remediation: backend code from last month now browseable)
import { FacilityOnboardingPage } from './pages/onboarding';
import { RegulatorySourceMonitorPage } from './pages/regulatory-sources';
import { InstanceCleanupLogPage } from './pages/instance-cleanup';

// Gantt View (DRR-070: PM timeline visualization)
import { GanttPage } from './pages/gantt';

// Calendar View (standalone task calendar across all workflows)
import { CalendarPage } from './pages/calendar/CalendarPage';
import { MySchedulePage } from './pages/calendar/MySchedulePage';

// Workflow Orchestration (DRR-072: Meta-orchestration visualization)
import { WorkflowOrchestrationPage } from './pages/workflow-orchestration';

// Data Source Browser (DRR-072: Chaos Ingestion)
import { DataSourceBrowserPage } from './pages/data-sources';

// Intake Studio (DRR-137: Multi-modal ingestion hub; renamed from Document Studio DRR-072)
import { IntakeStudioPage } from './pages/document-studio';

// PDF Studio (DRR-079: Full PDF Suite hub)
import { PdfStudioPage } from './pages/pdf-studio';

// Employee-Facing Pages (Layer 1 - "My Work")
import { MyInboxPage } from './pages/inbox';
import { MyDocumentsPage } from './pages/documents';
import { SigningCeremonyPage } from './pages/documents/SigningCeremonyPage';
import { PublicSigningPage } from './pages/signing/PublicSigningPage';
import { UsrnPortalPage } from './pages/usrn-portal';
import { MobileClockInPage } from './pages/mobile/MobileClockInPage';
import { DocumentViewerPage } from './pages/documents/DocumentViewerPage';
import { WorkflowInstanceProgress } from './pages/documents/components/WorkflowInstanceProgress';
import { ApprovalListPage, ApprovalInboxPage } from './pages/approvals';
import { MyProfilePage } from './pages/profile';

// Operations Pages - DRR-233 Track D (Schedule, Integration health monitoring)
import { ScheduleCalendarPage, IntegrationStatusPage } from './pages/operations';

// Admin Components
import { QueueManager } from './components/admin';

// Architect Components
import { PrimitiveGrid, SubgraphConstellation } from './components/architect';

// Route Guards
import { RequireArchitect } from './components/guards';

// Unified Primitive Form (25-primitive management)
import { UnifiedPrimitivePage } from './pages/primitives';

// Team Workspace + Management (DRR-061)
import { TeamWorkspacePage, TeamManagementPage } from './pages/team';

// Workforce Hub (grouped Actors + Teams, with detail pages)
import { WorkforcePage, ActorDetailPage, TeamDetailPage } from './pages/workforce';

// Constellation Visualization (DRR-085, DRR-090)
import { ConstellationPage } from './pages/constellation';

// Care Journey (DRR-126: subjectJourneyTimeline + entityJourneyGraph)
// DRR-309: Goal-centric workflow UI (JourneyView for instance-level progress)
import { JourneyPage, JourneyViewPage } from './pages/journey';

// Care Plan Pages (DRR-220: AI-assisted workflow with role isolation)
import { DonReviewPage } from './pages/care-plan';
import { DelegatedAssessmentPage } from './pages/workflow/DelegatedAssessmentPage';

// Coordination Engine (DRR-211: Shift planning, DRR-222: Flow Map)
import { ShiftPlanPage, FlowMapPage } from './pages/coordination';

// Domain-Agnostic Plan Pages (DRR-232, DRR-303)
import { ShiftWorkPage, PlanApprovalQueuePage, ControlledInventoryPage } from './pages/plans';

// Person-Centered View — DRR-134/135
import { MyPeoplePage } from './pages/people/MyPeoplePage';

// Ledger Pages (DRR-109: Three Token Types with Moov Settlement)
import { LedgerDashboardPage, LedgerFamilyPage, LedgerAuditPage } from './pages/ledger';

// Scope DAG Visualization (DRR-075)
import { ScopeHierarchyPage } from './pages/scope/ScopeHierarchyPage';

// My Access — role/scope/lens visibility panel (DRR-102)
import { MyAccessPage } from './pages/scope/MyAccessPage';

// CFSS Pages (DRR-279: Community First Services and Supports)
import {
  CfssDashboardPage,
  CfssTimesheetApprovalPage,
  CfssSupervisionPage,
  CfssPayrollPage,
  CfssWorkerManagementPage,
  CfssServiceAuthPage,
  CfssTrainingPage,
  CfssDocumentsPage,
  CfssIncidentPage,
  CfssCommunicationLogPage,
} from './pages/cfss';

// Self-service pages (user menu items)
import { MyTrainingPage } from './pages/training/MyTrainingPage';
import { StaffTrainingPage } from './pages/training/StaffTrainingPage';
import { MyPayrollPage, PayrollDashboardPage, TaxNotificationsPage } from './pages/payroll';

// Guardian Pages (DRR-199: Orphan dashboard surfacing)
import { GuardianDashboardPage, GuardianCompliancePage } from './pages/guardian';

// Cross-Tenant Exchange (DRR-105)
import { ExchangePage } from './pages/exchange';

// Enrollment (FERP-PII-052: WebAuthn PRF enrollment)
import { EnrollmentPage } from './pages/EnrollmentPage';

// PII Field Demo (FERP-PII-052: L1 field encryption testing)
import { L1FieldDemo } from './pii';

// PII Capture Page (DRR-302: Image PII extraction via Presidio OCR)
import { PiiCapturePage } from './pages/pii';

// Generic Subgraph Entity Form Page (DRR-055: Data-driven, works for ANY entity type)
// NOTE: No bespoke care pages — all 146 FHIR entity types (CarePlan, CareTeam,
// MedicationAdministration, AdverseEvent, etc.) render through SubgraphEntityFormPage
// + HealthcarePage tabs, driven by SubgraphMetadata and FormSchema configs.
import {
  SubgraphEntityCreatePage,
  SubgraphEntityEditPage,
  SubgraphEntityViewPage,
  SubgraphEntityApprovePage,
} from './pages/subgraphs/SubgraphEntityFormPage';

// Universal Subgraph Browser (DRR-055: data-driven entity browsing for ALL subgraphs)
import { SubgraphBrowserPage } from './pages/subgraphs';

// Tenant Setup Wizard (DRR-073: First-run experience)
import { TenantSetupWizard } from './features/tenant-setup/TenantSetupWizard';

// Tenant Management (DRR-073: FERP Operations onboarding dashboard)
import { TenantManagementPage as TenantOnboardingPage } from './pages/tenant-management';

// Legacy Archive Browser (DRR-092: Scan-gated tenant file access)
import { LegacyArchivePage } from './pages/legacy-archive/LegacyArchivePage';

// Component Explorer (surfaces ~80 components from @ferp/workflow-forms)
import { ComponentExplorerPage } from './pages/explorer';

// Dev Dashboard (replaces floating DevTools panels)
import { DevDashboardPage } from './pages/dev/DevDashboardPage';

// Registry Explorer (DRR-111: Architect visibility for config system)
import { RegistryExplorerPage } from './pages/architect/RegistryExplorerPage';

// Architect Pages (DRR-172: statute ingestion, proposal review, product builder)
import { StatuteIngestionPage } from './pages/architect/StatuteIngestionPage';
import { ProposalReviewPage } from './pages/architect/ProposalReviewPage';
import { ProductBuilderPage } from './pages/architect/ProductBuilderPage';

// Workflow Observer (PII-free workflow visibility)
import WorkflowObserverPage from './pages/architect/WorkflowObserverPage';
import WorkflowFlowPage from './pages/workflows/WorkflowFlowPage';
import DslEditorPage from './pages/architect/DslEditorPage';

// DRR-183: Ambient Operations Display (full-screen, no AppShell)
import { AmbientDisplayPage } from './pages/AmbientDisplayPage';

// Role-based dashboards (Staff = frontline, Supervisor = operations)
import { StaffDashboard } from './pages/dashboards/StaffDashboard';
import { SupervisorDashboard } from './pages/dashboards/SupervisorDashboard';
import { StaffDashboardPage, SupervisorDashboardPage } from './pages/dashboards';

// Terminology translation (maps FERP terms to industry-specific labels)
// DRR-189: useResolvedTerminology is the single source of truth for all terminology.
import { TerminologyProvider } from '@ferp/workflow-forms';
import type { TenantBranding as TerminologyTenantBranding } from '@ferp/graphql-client/src/tenantBrandingQueries';
import { useAdaptiveTenantBranding } from './hooks/useAdaptiveTenantBranding';
import { useResolvedTerminology } from './hooks/useResolvedTerminology';

// DATA-DRIVEN: Dynamic routing from SubgraphMetadata
import { useTenantSubgraphConfig } from './hooks/useTenantSubgraphConfig';
import { useDynamicRoutes } from './components/routing';

/**
 * Tenant Setup Page (DRR-073)
 * Wraps TenantSetupWizard with auth context to pass tenantId and navigation.
 */
function TenantSetupPage() {
  const { tenant } = useAuth();
  const navigate = useNavigate();

  return (
    <TenantSetupWizard
      tenantId={tenant?.id ?? 'unknown'}
      onComplete={() => navigate('/ferp-core/dashboard')}
      onCancel={() => navigate('/ferp-core/dashboard')}
    />
  );
}

/**
 * Protected Route wrapper - requires authentication
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Center h="100vh">
        <Stack ta="center" gap="md">
          <Loader size="xl" type="dots" />
          <Text size="sm" c="dimmed">Authenticating...</Text>
        </Stack>
      </Center>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// DRR-256: SubjectDashboardPage is available at /ferp-core/subjects as a dedicated
// person-centered view. The main dashboard remains unchanged for all view layers.

/**
 * DRR-135: Syncs backend-resolved view layer into AuthContext.
 *
 * Fetches myViewLayer (derived from role config `wall` field) and calls
 * setViewLayer — but only if the user hasn't manually overridden via
 * DevImpersonationPanel (portal:viewLayer in sessionStorage).
 *
 * Renders nothing; exists purely for the side effect.
 */
function ViewLayerSync() {
  const { setViewLayer, isAuthenticated, viewLayer: currentLayer } = useAuth();
  const { data, loading } = useMyViewLayerQuery({
    fetchPolicy: 'cache-and-network',
    skip: !isAuthenticated, // Don't query before login
  });

  useEffect(() => {
    // DEBUG: Track view layer sync
    console.log('[FERP-VIEWLAYER-DEBUG] ViewLayerSync:', {
      isAuthenticated,
      loading,
      backendLayer: data?.myViewLayer,
      currentLayer,
      devOverride: sessionStorage.getItem('portal:viewLayer'),
    });

    if (!data?.myViewLayer) return;
    // Don't override a DevPanel manual selection (dev mode only)
    const isDevMode = (window as any).__ENV__?.ENABLE_DEV_MODE === 'true';
    const devOverride = sessionStorage.getItem('portal:viewLayer');
    if (isDevMode && devOverride) {
      console.log('[FERP-VIEWLAYER-DEBUG] Skipping sync due to dev override:', devOverride);
      return;
    }
    const layer = data.myViewLayer.toLowerCase() as 'user' | 'admin' | 'architect';
    if (layer !== currentLayer) {
      console.log('[FERP-VIEWLAYER-DEBUG] Changing view layer:', currentLayer, '->', layer);
    }
    setViewLayer(layer);
  }, [data, setViewLayer, isAuthenticated, loading, currentLayer]);

  return null;
}

/**
 * Protected Routes with Dynamic Subgraph Routes
 *
 * Renders core static routes + dynamic subgraph routes from config.
 * This component must be inside AuthProvider to access tenant context.
 */
function ProtectedAppRoutes() {
  const { tenant } = useAuth();

  // DATA-DRIVEN: Get tenant's subgraph configuration
  const { config } = useTenantSubgraphConfig(tenant?.id ?? null);

  // Generate dynamic routes from SubgraphMetadata
  const dynamicRoutes = useDynamicRoutes(config);

  // Terminology: translates FERP primitive names to industry-specific labels
  const { branding } = useAdaptiveTenantBranding(tenant?.id ?? null);

  // DRR-189: Build flat concept→display map from resolved terminology.
  // This feeds TerminologyProvider so all useTerminology().translate() calls
  // get statutory-backed terminology instead of manually-authored labels.
  const { terms: resolvedTerms } = useResolvedTerminology();
  const terminologyMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const [concept, term] of Object.entries(resolvedTerms)) {
      map[concept] = term.display;
    }
    return map;
  }, [resolvedTerms]);

  return (
    <PageErrorBoundary pageName="FERP Portal">
    <ViewLayerSync />
    <TerminologyProvider terminologyMap={terminologyMap} branding={branding as unknown as TerminologyTenantBranding}>
    <MainLayout>
      <Routes>
        {/* Root redirect - route to first dynamic route or default dashboard */}
        <Route path="/" element={<Navigate to="/ferp-core/dashboard" replace />} />

        {/* ============================================
            NAMESPACE REDIRECTS (Data-Driven Compliance)
            Old non-namespaced routes redirect to namespaced equivalents.
            FERP Principle: Backend defines all routes with /{subgraph_id}/ prefix.
            ============================================ */}
        <Route path="/dashboard" element={<Navigate to="/ferp-core/dashboard" replace />} />
        <Route path="/inbox" element={<Navigate to="/ferp-core/inbox" replace />} />
        <Route path="/tasks" element={<Navigate to="/ferp-core/tasks" replace />} />
        <Route path="/tasks/:taskId" element={<Navigate to="/ferp-core/tasks" replace />} />
        <Route path="/documents" element={<Navigate to="/ferp-core/documents" replace />} />
        <Route path="/approvals" element={<Navigate to="/ferp-core/approvals" replace />} />
        <Route path="/profile" element={<Navigate to="/ferp-core/profile" replace />} />

        {/* ============================================
            ADMIN LAYER REDIRECTS (Data-Driven Compliance)
            Redirect /admin/* to /ferp-core/* namespaced routes.
            Backend SubgraphMetadata defines all actual routes.
            ============================================ */}
        <Route path="/admin/queues" element={<Navigate to="/ferp-core/queues" replace />} />
        <Route path="/admin/policies" element={<Navigate to="/ferp-core/policies" replace />} />
        <Route path="/admin/metrics" element={<Navigate to="/ferp-core/metrics" replace />} />
        <Route path="/admin/alerts" element={<Navigate to="/ferp-core/alerts" replace />} />
        <Route path="/admin/reports" element={<Navigate to="/ferp-core/reports" replace />} />
        <Route path="/admin/audits" element={<Navigate to="/ferp-core/audits" replace />} />
        <Route path="/admin/notifications" element={<Navigate to="/ferp-core/notifications" replace />} />
        <Route path="/admin/teams" element={<Navigate to="/ferp-core/teams-admin" replace />} />
        <Route path="/admin/form-schemas" element={<Navigate to="/ferp-core/form-schemas" replace />} />
        <Route path="/admin/compliance" element={<Navigate to="/ferp-core/compliance" replace />} />
        <Route path="/admin/risks" element={<Navigate to="/ferp-core/risks" replace />} />
        <Route path="/admin/evidence" element={<Navigate to="/ferp-core/evidence" replace />} />

        {/* Namespaced admin redirects (handles /ferp-core/admin/* URLs) */}
        <Route path="/ferp-core/admin/queues" element={<Navigate to="/ferp-core/queues" replace />} />
        <Route path="/ferp-core/admin/policies" element={<Navigate to="/ferp-core/policies" replace />} />
        <Route path="/ferp-core/admin/metrics" element={<Navigate to="/ferp-core/metrics" replace />} />
        <Route path="/ferp-core/admin/alerts" element={<Navigate to="/ferp-core/alerts" replace />} />
        <Route path="/ferp-core/admin/reports" element={<Navigate to="/ferp-core/reports" replace />} />
        <Route path="/ferp-core/admin/audits" element={<Navigate to="/ferp-core/audits" replace />} />
        <Route path="/ferp-core/admin/notifications" element={<Navigate to="/ferp-core/notifications" replace />} />
        <Route path="/ferp-core/admin/teams" element={<Navigate to="/ferp-core/teams-admin" replace />} />
        <Route path="/ferp-core/admin/form-schemas" element={<Navigate to="/ferp-core/form-schemas" replace />} />
        <Route path="/ferp-core/admin/compliance" element={<Navigate to="/ferp-core/compliance" replace />} />
        <Route path="/ferp-core/admin/risks" element={<Navigate to="/ferp-core/risks" replace />} />
        <Route path="/ferp-core/admin/evidence" element={<Navigate to="/ferp-core/evidence" replace />} />
        <Route path="/ferp-core/admin/staff" element={<ActorManagementPage />} />

        {/* Namespaced architect redirects (seed uses /ferp-core/architect/*, App.tsx uses /architect/*) */}
        <Route path="/ferp-core/architect/primitives" element={<Navigate to="/architect/primitives" replace />} />
        <Route path="/ferp-core/architect/templates" element={<Navigate to="/architect/templates" replace />} />
        <Route path="/ferp-core/architect/federation" element={<Navigate to="/architect/federation" replace />} />
        <Route path="/ferp-core/architect/field-constellation" element={<Navigate to="/architect/field-constellation" replace />} />
        <Route path="/ferp-core/architect/explorer" element={<Navigate to="/architect/explorer" replace />} />
        <Route path="/ferp-core/architect/workflow-registry" element={<Navigate to="/architect/workflow-registry" replace />} />
        <Route path="/ferp-core/architect/workflow-editor" element={<Navigate to="/architect/workflow-editor" replace />} />
        <Route path="/ferp-core/architect/form-editor" element={<Navigate to="/architect/form-editor" replace />} />
        <Route path="/ferp-core/architect/forms" element={<Navigate to="/architect/forms" replace />} />
        <Route path="/ferp-core/architect/tenant-management" element={<Navigate to="/architect/tenant-management" replace />} />
        <Route path="/ferp-core/architect/products" element={<Navigate to="/architect/products" replace />} />
        <Route path="/ferp-core/architect/scope-hierarchy" element={<Navigate to="/architect/scope-hierarchy" replace />} />
        <Route path="/ferp-core/architect/workflow-observer" element={<Navigate to="/architect/workflow-observer" replace />} />
        <Route path="/ferp-core/architect/registries" element={<Navigate to="/architect/registries" replace />} />
        <Route path="/ferp-core/architect/dsl-editor" element={<Navigate to="/architect/dsl-editor" replace />} />
        <Route path="/ferp-core/dev" element={<Navigate to="/dev" replace />} />

        {/* Static fallback for dashboard - prevents redirect loop when dynamic routes fail */}
        <Route path="/ferp-core/dashboard" element={<DashboardPage />} />

        {/* DRR-256: Unified Subject Dashboard - person-centered view for care staff */}
        <Route path="/ferp-core/subjects" element={<SubjectDashboardPage />} />

        {/* Work Surface - Universal subject-centered work interface */}
        <Route path="/ferp-core/work" element={<WorkSurfacePageConnected />} />

        {/* Static fallbacks for core primitive pages */}
        <Route path="/ferp-core/tasks" element={<TaskListPage />} />
        <Route path="/ferp-core/tasks/:taskId" element={<TaskDetailPage />} />
        <Route path="/ferp-core/task-templates" element={<TaskTemplatesPage />} />
        <Route path="/ferp-core/workflow-state" element={<WorkflowStateView />} />
        <Route path="/ferp-core/gantt" element={<GanttPage />} />
        <Route path="/ferp-core/calendar" element={<CalendarPage />} />
        <Route path="/ferp-core/my-schedule" element={<MySchedulePage />} />

        {/* Schedule nav group (DRR-232: promoted to top-level) */}
        <Route path="/schedule" element={<MySchedulePage />} />
        <Route path="/schedule/tasks" element={<CalendarPage />} />
        <Route path="/schedule/shifts" element={<ScheduleCalendarPage />} />
        <Route path="/ferp-core/states" element={<StateListPage />} />
        <Route path="/ferp-core/events" element={<EventListPage />} />
        <Route path="/ferp-core/actors" element={<ActorListPage />} />
        <Route path="/ferp-core/policies" element={<PageErrorBoundary pageName="Policies"><PolicyListPage /></PageErrorBoundary>} />
        <Route path="/ferp-core/roles" element={<RoleListPage />} />
        <Route path="/ferp-core/schedules" element={<ScheduleListPage />} />
        <Route path="/ferp-core/constraints" element={<ConstraintListPage />} />
        <Route path="/ferp-core/dependencies" element={<DependencyListPage />} />
        <Route path="/ferp-core/templates" element={<TemplateListPage />} />
        <Route path="/ferp-core/instances" element={<InstanceListPage />} />
        {/* DRR-165: Standalone InstanceDetailPage deprecated — operations tab in Tabulator popup */}
        <Route path="/ferp-core/instances/:instanceId" element={<Navigate to="/ferp-core/instances" replace />} />

        {/* DRR-241: Hiring Pipeline */}
        <Route path="/ferp-core/hiring/applicants" element={<ApplicantStatusPage />} />

        <Route path="/ferp-core/notifications" element={<NotificationListPage />} />
        <Route path="/ferp-core/contracts" element={<ContractListPage />} />
        <Route path="/ferp-core/transactions" element={<TransactionListPage />} />

        {/* DRR-109: Ledger Pages */}
        <Route path="/ferp-core/ledger" element={<LedgerDashboardPage />} />
        <Route path="/ferp-core/ledger/family" element={<LedgerFamilyPage />} />
        <Route path="/ferp-core/ledger/audit" element={<LedgerAuditPage />} />

        <Route path="/ferp-core/integrations" element={<IntegrationListPage />} />
        <Route path="/ferp-core/audits" element={<AuditListPage />} />
        <Route path="/ferp-core/analytics" element={<AnalyticsListPage />} />
        <Route path="/ferp-core/reconciliation" element={<ReconciliationDashboardPage />} />
        <Route path="/ferp-core/checkpoints" element={<CheckpointListPage />} />
        <Route path="/ferp-core/traces" element={<TraceListPage />} />
        <Route path="/ferp-core/queues" element={<QueueListPage />} />
        <Route path="/ferp-core/resources" element={<ResourceListPage />} />
        <Route path="/ferp-core/validations" element={<ValidationListPage />} />
        <Route path="/ferp-core/data-sources" element={<DataSourceBrowserPage />} />
        <Route path="/ferp-core/intake" element={<IntakeStudioPage />} />
        <Route path="/ferp-core/document-studio" element={<Navigate to="/ferp-core/intake" replace />} />
        <Route path="/ferp-core/pdf-studio" element={<PdfStudioPage />} />
        <Route path="/ferp-core/metrics" element={<MetricListPage />} />
        <Route path="/ferp-core/alerts" element={<AlertListPage />} />
        <Route path="/alerts/beacon" element={<BeaconDashboardPage />} />
        <Route path="/ferp-core/reports" element={<ReportListPage />} />
        <Route path="/ferp-core/compliance" element={<ComplianceListPage />} />
        <Route path="/ferp-core/compliance/readiness" element={<ReadinessDashboard />} />
        <Route path="/ferp-core/compliance/evidence" element={<ComplianceEvidencePage />} />
        <Route path="/ferp-core/compliance/practice-survey" element={<PracticeSurveyRunner />} />
        <Route path="/ferp-core/compliance/photo-compliance" element={<PhotoComplianceUpload />} />
        <Route path="/ferp-core/compliance/standards-packs" element={<StandardsPackPage />} />
        <Route path="/ferp-core/compliance/assessment-drift" element={<AssessmentEvidenceDashboard />} />
        <Route path="/ferp-core/compliance/manual" element={<ComplianceManualPage />} />
        <Route path="/ferp-core/compliance/audit-forms" element={<AuditFormsPage />} />
        <Route path="/ferp-core/compliance/audit-forms/:formId" element={<AuditFormsPage />} />
        <Route path="/ferp-core/rulesets" element={<RuleSetViewerPage />} />
        <Route path="/ferp-core/rulesets/admin" element={<RuleSetAdminPage />} />
        <Route path="/ferp-core/recommendations" element={<RecommendationsPage />} />
        <Route path="/ferp-core/risks" element={<RiskListPage />} />
        <Route path="/ferp-core/evidence" element={<EvidenceListPage />} />
        <Route path="/ferp-core/sagas" element={<SagaListPage />} />

        {/* Config Type List Pages (DRR-125 Sprint 4: tg-, cs-, rn-) */}
        <Route path="/ferp-core/task-groups" element={<TaskGroupListPage />} />
        <Route path="/ferp-core/task-groups/:groupId" element={<TaskGroupDetailPage />} />
        <Route path="/ferp-core/task-groups/:groupId/progress" element={<TaskGroupProgressPage />} />
        <Route path="/ferp-core/cascades" element={<CascadeListPage />} />
        <Route path="/ferp-core/reg-nodes" element={<RegNodeListPage />} />

        {/* Config Type List Pages (DRR-125 Part 12: st-, ft-) */}
        <Route path="/ferp-core/settings" element={<SettingListPage />} />
        <Route path="/ferp-core/form-templates" element={<FormTemplateListPage />} />

        {/* Config Browser Pages (seeded configs now browseable) */}
        <Route path="/ferp-core/briefing-templates" element={<BriefingTemplateListPage />} />
        <Route path="/ferp-core/governance" element={<GovernanceListPage />} />
        <Route path="/ferp-core/ui-schemas" element={<UiSchemaListPage />} />
        <Route path="/ferp-core/lenses" element={<LensCatalogPage />} />
        <Route path="/ferp-core/dashboard-schemas" element={<DashboardSchemaListPage />} />
        <Route path="/ferp-core/form-schema-overrides" element={<FormSchemaOverrideListPage />} />
        <Route path="/edi/templates" element={<EdiTemplateListPage />} />
        <Route path="/admin/branding" element={<TenantBrandingAdminPage />} />

        {/* Backend feature pages (gap remediation) */}
        <Route path="/ferp-core/onboarding" element={<FacilityOnboardingPage />} />
        <Route path="/ferp-core/regulatory-sources" element={<RegulatorySourceMonitorPage />} />
        <Route path="/ferp-core/instance-cleanup" element={<InstanceCleanupLogPage />} />
        <Route path="/ferp-core/workflows/studio" element={<WorkflowStudioPage />} />
        <Route path="/ferp-core/workflows/master" element={<MasterWorkflowPage />} />
        <Route path="/ferp-core/workflows/:workflowId" element={<WorkflowDetailPage />} />
        <Route path="/ferp-core/workflows" element={<WorkflowListPage />} />
        <Route path="/ferp-core/flow/:workflowId" element={<WorkflowFlowPage />} />
        <Route path="/ferp-core/workflow-orchestration" element={<WorkflowOrchestrationPage />} />

        {/* Employee-Facing Pages (Layer 1 - "My Work") */}
        <Route path="/ferp-core/inbox" element={<MyInboxPage />} />
        <Route path="/ferp-core/documents" element={<MyDocumentsPage />} />
        <Route path="/ferp-core/documents/ceremony/:ceremonyId" element={<SigningCeremonyPage />} />
        <Route path="/ferp-core/documents/:documentId" element={<DocumentViewerPage />} />
        <Route path="/ferp-core/subjects/:subjectId/checklist" element={<WorkflowInstanceProgress />} />
        <Route path="/ferp-core/approvals" element={<ApprovalListPage />} />
        <Route path="/ferp-core/approvals/inbox" element={<ApprovalInboxPage />} />
        <Route path="/ferp-core/profile" element={<MyProfilePage />} />

        {/* Operations Pages - DRR-233 Track D */}
        <Route path="/ferp-core/operations/schedules" element={<ScheduleCalendarPage />} />
        <Route path="/ferp-core/operations/integrations" element={<IntegrationStatusPage />} />

        {/* Team Workspace + Management (DRR-061) */}
        <Route path="/ferp-core/team" element={<TeamWorkspacePage />} />
        <Route path="/ferp-core/teams-admin" element={<TeamManagementPage />} />
        <Route path="/ferp-core/admin/actors" element={<ActorManagementPage />} />

        {/* Workforce Hub — grouped Actors + Teams (CONFIGURE phase) */}
        <Route path="/ferp-core/workforce" element={<WorkforcePage />} />
        <Route path="/ferp-core/workforce/actors/:actorId" element={<ActorDetailPage />} />
        <Route path="/ferp-core/workforce/teams/:teamCode" element={<TeamDetailPage />} />
        <Route path="/ferp-core/form-schemas" element={<FormSchemaAdminPage />} />

        {/* Entity Type Manager — preset-based progressive disclosure */}
        <Route path="/admin/entity-types" element={<SubgraphEntityManagerPage />} />
        <Route path="/architect/entity-types" element={<SubgraphEntityManagerPage />} />
        <Route path="/ferp-core/entity-types" element={<SubgraphEntityManagerPage />} />

        {/* Constellation Visualization (DRR-085, DRR-090) */}
        <Route path="/ferp-core/constellation" element={<ConstellationPage />} />

        {/* Spatial Layout Editor — DRR-179 (admin/architect layer) */}
        <Route path="/ferp-core/spatial-layout" element={<SpatialPinningEditor />} />

        {/* Care Journey — DRR-126 (layer: admin, DON/ALD and above) */}
        <Route path="/ferp-core/journey" element={<JourneyPage />} />

        {/* Goal-Centric Journey View — DRR-309 (instance-level progress visualization) */}
        <Route path="/ferp-core/journey/:instanceId" element={<JourneyViewPage />} />

        {/* Delegated Assessment — DRR-220 (generic data gathering for AI-assisted workflows) */}
        <Route path="/ferp-core/workflow/delegated-assessment" element={<DelegatedAssessmentPage />} />

        {/* DoN Care Plan Review — DRR-220 (DoN finalizes USRN-approved care plans) */}
        <Route path="/ferp-core/care-plan/don-review" element={<DonReviewPage />} />

        {/* Coordination Engine — DRR-211 (shift plan with ML-ready weights) */}
        <Route path="/ferp-core/coordination/shift-plan" element={<ShiftPlanPage />} />

        {/* Domain-Agnostic Plan Pages — DRR-232, DRR-303 */}
        <Route path="/ferp-core/shift-work" element={<ShiftWorkPage />} />
        <Route path="/ferp-core/plan-approval" element={<PlanApprovalQueuePage />} />
        <Route path="/ferp-core/controlled-inventory" element={<ControlledInventoryPage />} />

        {/* Tenant Flow Map — DRR-222 (multi-view work stream visualization) */}
        <Route path="/ferp-core/flow-map" element={<FlowMapPage />} />

        {/* Person-Centered View — DRR-134/135 */}
        <Route path="/ferp-core/people" element={<MyPeoplePage />} />

        {/* Source Coordinate Map Review (extraction provenance) */}
        <Route path="/admin/source-maps" element={<SourceMapListPage />} />
        <Route path="/admin/witness-queue" element={<WitnessQueuePage />} />
        <Route path="/admin/source-maps/:mappingId" element={<SourceMapReviewPage />} />

        {/* ============================================
            TENANT SETUP WIZARD (DRR-073)
            First-run experience for new tenant admins.
            Pre-provisioned tenants skip this; self-service
            tenants see it on first login if no workflows exist.
            ============================================ */}
        <Route path="/setup" element={<TenantSetupPage />} />

        {/* ============================================
            SYNC DASHBOARD (DRR-113)
            Offline sync sessions, Blake3 chain verification,
            conflict resolution queue.
            ============================================ */}
        <Route path="/sync" element={<SyncDashboardPage />} />

        {/* ============================================
            ENROLLMENT (FERP-PII-052)
            WebAuthn PRF enrollment for L1/L1G protection
            ============================================ */}
        <Route path="/enrollment" element={<EnrollmentPage />} />

        {/* ============================================
            PII DEMO (FERP-PII-052)
            L1 field encryption testing with WebAuthn PRF
            ============================================ */}
        <Route path="/pii-demo" element={<L1FieldDemo />} />

        {/* ============================================
            PII CAPTURE (DRR-302)
            Camera-based PII extraction via Presidio OCR
            ============================================ */}
        <Route path="/ferp-core/pii-capture" element={<PiiCapturePage />} />

        {/* ============================================
            GENERIC SUBGRAPH ENTITY FORMS (DRR-055)
            ONE route pattern serves ALL entity types
            from ALL subgraphs. Metadata-driven.
            ============================================ */}
        <Route path="/admin/:subgraph/entities/:entityType/create" element={<SubgraphEntityCreatePage />} />
        <Route path="/admin/:subgraph/entities/:entityType/:id/edit" element={<SubgraphEntityEditPage />} />
        <Route path="/admin/:subgraph/entities/:entityType/:id/approve" element={<SubgraphEntityApprovePage />} />
        <Route path="/admin/:subgraph/entities/:entityType/:id" element={<SubgraphEntityViewPage />} />

        {/* ============================================
            FHIR SUBGRAPH - User Layer Care Routes
            Static fallbacks for Care Management nav group.
            These resolve BEFORE dynamic routes load.
            ============================================ */}
        <Route path="/fhir/residents" element={<SubgraphBrowserPage />} />
        <Route path="/fhir/care-plans" element={<SubgraphBrowserPage />} />
        <Route path="/fhir/assessments" element={<SubgraphBrowserPage />} />
        <Route path="/fhir/incidents" element={<SubgraphBrowserPage />} />
        <Route path="/fhir/patients" element={<SubgraphBrowserPage />} />
        <Route path="/fhir/encounters" element={<SubgraphBrowserPage />} />
        <Route path="/fhir/observations" element={<SubgraphBrowserPage />} />
        <Route path="/fhir/medications" element={<SubgraphBrowserPage />} />
        <Route path="/fhir/claims" element={<SubgraphBrowserPage />} />
        <Route path="/fhir/conditions" element={<SubgraphBrowserPage />} />
        <Route path="/fhir/allergy-intolerances" element={<SubgraphBrowserPage />} />
        <Route path="/fhir/medication-administration" element={<SubgraphBrowserPage />} />
        <Route path="/fhir/documents" element={<SubgraphBrowserPage />} />

        {/* ============================================
            FHIR SUBGRAPH - Admin Layer Routes
            Admin/DON/ALD entity management.
            Entity visibility filtered by backend scopes.
            ============================================ */}
        <Route path="/admin/fhir" element={<SubgraphBrowserPage />} />
        <Route path="/admin/healthcare" element={<SubgraphBrowserPage />} />

        {/* ============================================
            EDI SUBGRAPH - Admin Layer Routes
            Static fallbacks for EDI billing navigation.
            ============================================ */}
        <Route path="/edi/claims" element={<SubgraphBrowserPage />} />
        <Route path="/edi/remittance" element={<SubgraphBrowserPage />} />
        <Route path="/edi/eligibility" element={<SubgraphBrowserPage />} />
        <Route path="/edi/acknowledgments" element={<SubgraphBrowserPage />} />
        <Route path="/edi/partners" element={<SubgraphBrowserPage />} />

        {/* ============================================
            WEARABLES SUBGRAPH - Vuzix Smart Glasses Routes
            ============================================ */}
        <Route path="/wearables/my-device" element={<SubgraphBrowserPage />} />
        <Route path="/wearables/devices" element={<SubgraphBrowserPage />} />
        <Route path="/wearables/sessions" element={<SubgraphBrowserPage />} />
        <Route path="/wearables/captures" element={<SubgraphBrowserPage />} />
        <Route path="/wearables/commands" element={<SubgraphBrowserPage />} />
        <Route path="/wearables/beacons" element={<SubgraphBrowserPage />} />
        <Route path="/wearables/proximity" element={<SubgraphBrowserPage />} />

        {/* ============================================
            USER LAYER COMPLIANCE - Staff Training Page
            Supervisor view of all staff training status.
            ============================================ */}
        <Route path="/ferp-core/training" element={<StaffTrainingPage />} />

        {/* ============================================
            PAYROLL SUBGRAPH - Time, Pay & Benefits Routes
            Hillside Harmony: staffing, time tracking, payroll.
            ============================================ */}
        {/* Payroll User Layer */}
        <Route path="/payroll/time-entries" element={<SubgraphBrowserPage />} />
        <Route path="/payroll/shifts" element={<SubgraphBrowserPage />} />
        <Route path="/payroll/leave-requests" element={<SubgraphBrowserPage />} />
        <Route path="/payroll/pto-balances" element={<SubgraphBrowserPage />} />
        <Route path="/payroll/pay-stubs" element={<SubgraphBrowserPage />} />
        <Route path="/payroll/benefits" element={<SubgraphBrowserPage />} />
        {/* Payroll Admin Layer */}
        <Route path="/payroll/runs" element={<SubgraphBrowserPage />} />
        <Route path="/payroll/tax-filings" element={<SubgraphBrowserPage />} />
        <Route path="/payroll/deductions" element={<SubgraphBrowserPage />} />
        <Route path="/payroll/exceptions" element={<SubgraphBrowserPage />} />
        <Route path="/payroll/timesheets" element={<SubgraphBrowserPage />} />
        <Route path="/payroll/punch-records" element={<SubgraphBrowserPage />} />
        <Route path="/payroll/schedules" element={<SubgraphBrowserPage />} />
        <Route path="/payroll/overtime" element={<SubgraphBrowserPage />} />
        <Route path="/payroll/leave-policies" element={<SubgraphBrowserPage />} />
        <Route path="/payroll/employees" element={<SubgraphBrowserPage />} />

        {/* ============================================
            WEBSITE SUBGRAPH - Builder & Deployment Routes
            ============================================ */}
        <Route path="/website/my-sites" element={<SubgraphBrowserPage />} />
        <Route path="/website/builder" element={<SubgraphBrowserPage />} />
        <Route path="/website/admin/deployments" element={<SubgraphBrowserPage />} />
        <Route path="/website/admin/domains" element={<SubgraphBrowserPage />} />
        <Route path="/website/architect/themes" element={<SubgraphBrowserPage />} />
        <Route path="/website/architect/templates" element={<SubgraphBrowserPage />} />

        {/* ============================================
            ACCOUNTING — Daily Bookkeeping
            ============================================ */}
        <Route path="/accounting/journal-entries" element={<SubgraphBrowserPage />} />
        <Route path="/accounting/gl-accounts" element={<SubgraphBrowserPage />} />
        <Route path="/accounting/vendors" element={<SubgraphBrowserPage />} />
        <Route path="/accounting/customers" element={<SubgraphBrowserPage />} />
        <Route path="/accounting/reports" element={<SubgraphBrowserPage />} />

        {/* ============================================
            ERP — Advanced Financial Management
            ============================================ */}
        <Route path="/accounting/orders" element={<SubgraphBrowserPage />} />
        <Route path="/accounting/invoices" element={<SubgraphBrowserPage />} />
        <Route path="/accounting/payments" element={<SubgraphBrowserPage />} />
        <Route path="/accounting/billing-accounts" element={<SubgraphBrowserPage />} />
        <Route path="/accounting/budgets" element={<SubgraphBrowserPage />} />
        <Route path="/accounting/fixed-assets" element={<SubgraphBrowserPage />} />
        <Route path="/accounting/products" element={<SubgraphBrowserPage />} />
        <Route path="/accounting/tax-authorities" element={<SubgraphBrowserPage />} />
        <Route path="/accounting/ledger" element={<SubgraphBrowserPage />} />

        {/* ERP nav path redirects — nav uses /erp/*, routes use /accounting/* */}
        <Route path="/erp/inventory" element={<SubgraphBrowserPage />} />
        <Route path="/erp/vendors" element={<SubgraphBrowserPage />} />
        <Route path="/erp/orders" element={<Navigate to="/accounting/orders" replace />} />
        <Route path="/erp/products" element={<Navigate to="/accounting/products" replace />} />

        {/* ============================================
            DYNAMIC SUBGRAPH ROUTES (DATA-DRIVEN)
            Generated from SubgraphMetadata.allRoutes
            Replaces hardcoded subgraph-specific routes
            ============================================ */}
        {dynamicRoutes}

        {/* ============================================
            ARCHITECT LAYER (Layer 3) - "Primitive Composition"
            25-Primitive Grid and Federation Overview
            ============================================ */}

        {/* Unified Primitive Form - comprehensive 25-primitive management */}
        <Route path="/architect/primitives" element={<RequireArchitect><UnifiedPrimitivePage /></RequireArchitect>} />

        {/* Legacy Primitive Grid (kept for reference) */}
        <Route path="/architect/primitives-legacy" element={<RequireArchitect><PrimitiveGrid /></RequireArchitect>} />

        {/* Template Management → redirects to Workflow Studio */}
        <Route path="/architect/templates" element={<Navigate to="/ferp-core/workflows/studio" replace />} />

        {/* Workflow Registry + LLM Editors (DRR-056/057) */}
        <Route path="/architect/workflow-registry" element={<RequireArchitect><WorkflowRegistryPage /></RequireArchitect>} />
        <Route path="/architect/workflow-editor" element={<RequireArchitect><LlmWorkflowEditorPage /></RequireArchitect>} />
        <Route path="/architect/form-editor" element={<RequireArchitect><LlmFormSchemaEditorPage /></RequireArchitect>} />

        {/* Tenant Management (DRR-073: FERP Operations onboarding) */}
        <Route path="/architect/tenant-management" element={<RequireArchitect><TenantOnboardingPage /></RequireArchitect>} />

        {/* Legacy Archive Browser (DRR-092: scan-gated tenant legacy files) */}
        <Route path="/admin/legacy-archive" element={<RequireArchitect><LegacyArchivePage /></RequireArchitect>} />

        {/* Component Explorer (surfaces ~80 components from @ferp/workflow-forms) */}
        <Route path="/architect/explorer" element={<RequireArchitect><ComponentExplorerPage /></RequireArchitect>} />

        {/* Form Gallery - DRR-074 migration review: all Enhanced/Validated/Manager forms */}
        <Route path="/architect/forms" element={<RequireArchitect><FormGalleryPage /></RequireArchitect>} />

        {/* Product Catalog - DRR-095/096: Layer 2 config set browser */}
        <Route path="/architect/products" element={<RequireArchitect><ProductCatalogPage /></RequireArchitect>} />

        {/* Federation - Subgraph Constellation (includes ErrorTracePanel - CRITICAL SECURITY) */}
        <Route path="/architect/federation" element={<RequireArchitect><SubgraphConstellation /></RequireArchitect>} />

        {/* Field Constellation - Primitive field category visualization */}
        <Route path="/architect/field-constellation" element={<RequireArchitect><PrimitiveConstellationPage /></RequireArchitect>} />

        {/* Scope Hierarchy - DAG visualization of org structure + assignments (DRR-075) */}
        <Route path="/architect/scope-hierarchy" element={<RequireArchitect><ScopeHierarchyPage /></RequireArchitect>} />

        {/* My Access - Role/scope/lens visibility panel (DRR-102) — ALL authenticated users */}
        <Route path="/my-access" element={<MyAccessPage />} />

        {/* Self-service pages: training status + payroll (scope-gated via setting user_menu_items) */}
        <Route path="/ferp-core/my-training" element={<MyTrainingPage />} />
        <Route path="/ferp-core/my-payroll" element={<MyPayrollPage />} />

        {/* CFSS Pages (DRR-279: Community First Services and Supports) */}
        <Route path="/cfss" element={<CfssDashboardPage />} />
        <Route path="/cfss/timesheets" element={<CfssTimesheetApprovalPage />} />
        <Route path="/cfss/supervision" element={<CfssSupervisionPage />} />
        <Route path="/cfss/payroll" element={<CfssPayrollPage />} />
        <Route path="/cfss/workers" element={<CfssWorkerManagementPage />} />
        <Route path="/cfss/authorizations" element={<CfssServiceAuthPage />} />
        <Route path="/cfss/training" element={<CfssTrainingPage />} />
        <Route path="/cfss/documents" element={<CfssDocumentsPage />} />
        <Route path="/cfss/incidents" element={<CfssIncidentPage />} />
        <Route path="/cfss/communication" element={<CfssCommunicationLogPage />} />

        {/* RBAC Dashboard - Consolidated role management (roles, permissions, hierarchy, audit, JIT) */}
        <Route path="/admin/rbac" element={<RBACDashboardPage />} />

        {/* Role Access Comparison - Admin tool for multi-role field visibility diff (DRR-103) */}
        <Route path="/admin/role-access" element={<RoleAccessComparisonPage />} />

        {/* ============================================
            SURFACED DASHBOARDS (DRR-199: 10 orphan dashboards now accessible)
            ============================================ */}

        {/* Saga Monitoring - Distributed transaction orchestration */}
        <Route path="/admin/saga-monitoring" element={<SagaMonitoringPage />} />

        {/* Executive Safety - C-level safety metrics and compliance posture */}
        <Route path="/admin/executive-safety" element={<ExecutiveSafetyPage />} />

        {/* Cache Management - Apollo/Valkey cache performance and invalidation */}
        <Route path="/admin/cache-management" element={<CacheManagementPage />} />

        {/* Audit Intelligence - Executive-level audit insights */}
        <Route path="/admin/audit-intelligence" element={<CLevelAuditPage />} />

        {/* Job Monitoring - Background job status and queue metrics */}
        <Route path="/admin/job-monitoring" element={<JobMonitoringPage />} />

        {/* Queue Management - Task queues, dead letter, saga coordination */}
        <Route path="/admin/queues" element={<QueueManagerPage />} />

        {/* System Health - Real-time system health and resource utilization */}
        <Route path="/admin/system-health" element={<SystemHealthPage />} />

        {/* Optimization - AI-powered optimization recommendations */}
        <Route path="/admin/optimization" element={<OptimizationPage />} />

        {/* Task Analytics - Task completion rates and bottleneck analysis */}
        <Route path="/admin/task-analytics" element={<TaskAnalyticsPage />} />

        {/* Process Analytics - Process execution patterns and cycle times */}
        <Route path="/admin/process-analytics" element={<ProcessAnalyticsPage />} />

        {/* Approval Queue - Pending approvals and SLA monitoring */}
        <Route path="/admin/approval-queue" element={<ApprovalQueuePage />} />

        {/* Config Proposal Queue - DRR-227 Tenant Architect Dual-Approval */}
        <Route path="/admin/config-proposals" element={<ConfigProposalQueuePage />} />

        {/* ============================================
            ADDITIONAL SURFACED DASHBOARDS (DRR-199 continuation)
            ============================================ */}

        {/* Emergency Participants - Safety-critical participant tracking during emergencies */}
        <Route path="/admin/emergency-participants" element={<EmergencyParticipantPage />} />

        {/* Safety Events - Real-time safety event monitoring and incident tracking */}
        <Route path="/admin/safety-events" element={<SafetyEventsPage />} />

        {/* Validation Review - Review and approve validation results */}
        <Route path="/admin/validation-review" element={<ValidationReviewPage />} />

        {/* LLM Comparison - Compare outputs across multiple LLM providers */}
        <Route path="/admin/llm-comparison" element={<MultiLLMComparisonPage />} />

        {/* Policy Analytics - Policy execution metrics and compliance analytics */}
        <Route path="/admin/policy-analytics" element={<PolicyAnalyticsPage />} />

        {/* Regulatory Governance - Regulatory compliance status and audit trails */}
        <Route path="/admin/regulatory-governance" element={<RegulatoryGovernancePage />} />

        {/* Production Monitoring - Production system health and incident tracking */}
        <Route path="/admin/production-monitoring" element={<ProductionMonitoringPage />} />

        {/* Workflow Composition - Visual workflow composition and fragment management */}
        <Route path="/admin/workflow-composition" element={<WorkflowCompositionPage />} />

        {/* Actor Runtime - Actor model runtime monitoring */}
        <Route path="/admin/actor-runtime" element={<ActorRuntimePage />} />

        {/* Actor Descriptors - Actor descriptor management */}
        <Route path="/admin/actor-descriptors" element={<ActorDescriptorPage />} />

        {/* Draft Management - Workflow draft management */}
        <Route path="/admin/drafts" element={<DraftManagementPage />} />

        {/* Comprehensive Dashboard - Full system overview */}
        <Route path="/admin/comprehensive" element={<ComprehensiveDashboardPage />} />

        {/* Secure System Health - Security-focused health monitoring */}
        <Route path="/admin/secure-health" element={<SecureSystemHealthPage />} />

        {/* Tabulator Lifecycle - Advanced tabular data views */}
        <Route path="/admin/tabulator-lifecycle" element={<TabulatorLifecyclePage />} />

        {/* Primitive Composition - 25-primitive composition management */}
        <Route path="/admin/primitive-composition" element={<PrimitiveCompositionPage />} />

        {/* Primitive Registry - Universal primitive registry */}
        <Route path="/admin/primitive-registry" element={<PrimitiveRegistryPage />} />

        {/* Domain Configuration - Domain-agnostic config management */}
        <Route path="/admin/domain-config" element={<DomainConfigPage />} />

        {/* Compliance Configuration - Regulatory threshold configuration */}
        <Route path="/admin/compliance-config" element={<ComplianceConfigPage />} />

        {/* Governance Dashboard - 5 governance primitives */}
        <Route path="/admin/governance-dashboard" element={<GovernanceDashboardPage />} />

        {/* Governance Context - Governance execution context */}
        <Route path="/admin/governance-context" element={<GovernanceContextPage />} />

        {/* Saga Metrics - Saga primitive metrics */}
        <Route path="/admin/saga-metrics" element={<SagaMetricsPage />} />

        {/* Monitoring Dashboard - Real-time system monitoring */}
        <Route path="/admin/monitoring" element={<MonitoringDashboardPage />} />

        {/* Role Management - Complete RBAC management */}
        <Route path="/admin/role-management" element={<RoleManagementPage />} />

        {/* Integration Management - External system integrations */}
        <Route path="/admin/integration-management" element={<IntegrationManagementPage />} />

        {/* Resource Management - Resource allocation management */}
        <Route path="/admin/resource-management" element={<ResourceManagementPage />} />

        {/* Escalation Management - Escalation path management */}
        <Route path="/admin/escalation-management" element={<EscalationManagementPage />} />

        {/* Schedule Management - Workflow schedule management */}
        <Route path="/admin/schedule-management" element={<ScheduleManagementPage />} />

        {/* Policy Management - Business rule management */}
        <Route path="/admin/policy-management" element={<PolicyManagementPage />} />

        {/* DRR-199 Batch 7: Orphan Dashboard Surfacing */}
        {/* Expert Routing - Intelligent work assignment and capacity management */}
        <Route path="/admin/expert-routing" element={<ExpertRoutingPage />} />

        {/* Task Approval - Task approval workflows and SLA tracking */}
        <Route path="/admin/task-approval" element={<TaskApprovalPage />} />

        {/* Approval Policies - Approval policy configuration and escalation rules */}
        <Route path="/admin/approval-policies" element={<ApprovalPoliciesPage />} />

        {/* DRR-125 Batch 8: Complete Orphan Dashboard Surfacing */}
        <Route path="/admin/tabulator-entity" element={<TabulatorEntityPage />} />
        <Route path="/admin/master-workflow-dashboard" element={<MasterWorkflowDashboardPage />} />
        <Route path="/admin/template-workflow" element={<TemplateWorkflowPage />} />
        <Route path="/admin/conversion-progress" element={<ConversionProgressPage />} />
        <Route path="/admin/extraction-review" element={<ExtractionReviewPage />} />
        <Route path="/admin/dashboard-enhanced" element={<DashboardEnhancedPage />} />
        <Route path="/admin/graphql-widgets" element={<GraphQLWidgetsPage />} />
        <Route path="/admin/lifecycle-widgets" element={<LifecycleWidgetsPage />} />
        <Route path="/admin/composition-engine" element={<CompositionEnginePage />} />
        <Route path="/admin/complex-process" element={<ComplexProcessPage />} />
        <Route path="/admin/performance-optimization" element={<PerformanceOptimizationPage />} />
        <Route path="/admin/chaos-templates" element={<ChaosTemplatePage />} />
        <Route path="/admin/sprint7" element={<Sprint7DashboardPage />} />
        <Route path="/admin/ambient-dashboard" element={<AmbientDashboardPage />} />
        <Route path="/admin/qapi-dashboard" element={<QapiDashboardPage />} />
        <Route path="/admin/compliance-dashboard" element={<ComplianceDashboardPage />} />
        <Route path="/admin/production-system-health" element={<ProductionSystemHealthPage />} />

        {/* DRR-233 Track C: Search and Expert Finder */}
        <Route path="/admin/search" element={<SearchPage />} />

        {/* DRR-238 Phase 2: Terminology Override Management */}
        <Route path="/admin/terminology" element={<TerminologyPage />} />

        {/* DRR-317: Auth State Observability & Drift Detection */}
        <Route path="/admin/auth-observability" element={<AuthObservabilityPage />} />

        {/* Payroll Dashboard - Payroll run management */}
        <Route path="/payroll/dashboard" element={<PayrollDashboardPage />} />

        {/* Tax Notifications - Regulatory tax notification management */}
        <Route path="/payroll/tax-notifications" element={<TaxNotificationsPage />} />

        {/* Guardian Dashboard - Guardian enrollment and management */}
        <Route path="/guardian/dashboard" element={<GuardianDashboardPage />} />

        {/* Guardian Compliance - Guardian compliance review */}
        <Route path="/guardian/compliance" element={<GuardianCompliancePage />} />

        {/* Staff and Supervisor Dashboard Pages (full-page versions) */}
        <Route path="/dashboards/staff" element={<StaffDashboardPage />} />
        <Route path="/dashboards/supervisor" element={<SupervisorDashboardPage />} />

        {/* Cross-Tenant Exchange — DRR-105 (requires exchange:read scope) */}
        <Route path="/exchange" element={<ExchangePage />} />

        {/* Registry Explorer - DRR-111: Config type registry browser for architects */}
        <Route path="/architect/registries" element={<RequireArchitect><RegistryExplorerPage /></RequireArchitect>} />

        {/* Dev Dashboard - Unified developer tools (replaces floating DevTools) */}
        <Route path="/dev" element={<RequireArchitect><DevDashboardPage /></RequireArchitect>} />

        {/* Workflow Observer - PII-free workflow visibility (dual-gate: scope + header) */}
        <Route path="/architect/workflow-observer" element={<RequireArchitect><WorkflowObserverPage /></RequireArchitect>} />

        {/* DSL Editor - WHEN/THEN policy rule compiler (Phase 12-13) */}
        <Route path="/architect/dsl-editor" element={<RequireArchitect><DslEditorPage /></RequireArchitect>} />

        {/* Statute Ingestion - LLM-assisted regulation-to-config pipeline */}
        <Route path="/architect/statute-ingestion" element={<RequireArchitect><StatuteIngestionPage /></RequireArchitect>} />

        {/* Proposal Review - Coverage gap analysis and config proposal approval */}
        <Route path="/architect/proposal-review" element={<RequireArchitect><ProposalReviewPage /></RequireArchitect>} />

        {/* Product Builder - Product catalog viewer with coverage checks */}
        <Route path="/architect/product-builder" element={<RequireArchitect><ProductBuilderPage /></RequireArchitect>} />

        {/* Role-based dashboards — generic, content driven by backend data */}
        <Route path="/staff" element={<StaffDashboard />} />
        <Route path="/supervisor" element={<SupervisorDashboard />} />

        {/* ============================================
            PRIMITIVE ROUTES BY LIFECYCLE PHASE
            Direct access to all 25 primitives
            ============================================ */}

        {/* DESIGN Phase (5 primitives) */}
        <Route path="/primitives/workflows" element={<WorkflowListPage />} />
        <Route path="/primitives/templates" element={<TemplateListPage />} />
        <Route path="/primitives/constraints" element={<ConstraintListPage />} />
        <Route path="/primitives/dependencies" element={<DependencyListPage />} />
        <Route path="/primitives/forms" element={<PrimitiveGrid />} />

        {/* CONFIGURE Phase (5 primitives) */}
        <Route path="/primitives/actors" element={<ActorListPage />} />
        <Route path="/primitives/policies" element={<PageErrorBoundary pageName="Policies"><PolicyListPage /></PageErrorBoundary>} />
        <Route path="/primitives/permissions" element={<RoleListPage />} />
        <Route path="/primitives/queues" element={<QueueManager />} />
        <Route path="/primitives/schedules" element={<ScheduleListPage />} />

        {/* EXECUTE Phase (5 primitives) */}
        <Route path="/primitives/tasks" element={<TaskListPage />} />
        <Route path="/primitives/states" element={<StateListPage />} />
        <Route path="/primitives/instances" element={<InstanceListPage />} />
        <Route path="/primitives/events" element={<EventListPage />} />
        <Route path="/primitives/sagas" element={<SagaListPage />} />
        <Route path="/primitives/dagnodes" element={<DAGNodeListPage />} />

        {/* MONITOR Phase (5 primitives) */}
        <Route path="/primitives/metrics" element={<MetricListPage />} />
        <Route path="/primitives/alerts" element={<AlertListPage />} />
        <Route path="/primitives/audits" element={<AuditListPage />} />
        <Route path="/primitives/reports" element={<ReportListPage />} />
        <Route path="/primitives/notifications" element={<NotificationListPage />} />
        <Route path="/primitives/transactions" element={<TransactionListPage />} />

        {/* GOVERN Phase (5 primitives) */}
        <Route path="/primitives/contracts" element={<ContractListPage />} />
        <Route path="/primitives/compliance" element={<ComplianceListPage />} />
        <Route path="/primitives/risks" element={<RiskListPage />} />
        <Route path="/primitives/evidence" element={<EvidenceListPage />} />
        <Route path="/primitives/integrations" element={<IntegrationListPage />} />

        {/* Catch-all - redirect to dashboard */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </MainLayout>
    </TerminologyProvider>
    </PageErrorBoundary>
  );
}

/**
 * Application Routes organized by Three-Layer Interface
 */
function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/sign/:token" element={<PublicSigningPage />} />
      {/* DRR-220: USRN Portal - External clinical reviewer via magic link */}
      <Route path="/usrn-portal" element={<UsrnPortalPage />} />
      {/* DRR-221: Mobile EVV clock-in via SMS deep link */}
      <Route path="/clock-in/:scheduleId" element={<MobileClockInPage />} />

      {/* DRR-183: Ambient display — protected but NO AppShell (full-screen kiosk) */}
      <Route
        path="/ambient"
        element={
          <ProtectedRoute>
            <AmbientDisplayPage />
          </ProtectedRoute>
        }
      />

      {/* Protected routes with dynamic subgraph routing */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <ProtectedAppRoutes />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

/**
 * Root App component
 */
export function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AuthProvider>
        <ViewContextProvider>
          <SimulationProvider>
            <ActorNameProvider>
              <FhirLabelsProvider>
                <AppRoutes />
              </FhirLabelsProvider>
            </ActorNameProvider>
          </SimulationProvider>
        </ViewContextProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
